/**
 * Core / Plugin: Component states
 * -----------------------------------------------------------------------------
 * Vue plugin to extend Vue instance with component methods.
 *
 */
import { mapActions, mapState } from 'pinia';

import { convertToHandle } from '~/helpers/convert';

import useComponentsStore from '~stores/components';

/**
 * Expose install method.
 */
export default {
  install: (app) => {
    app.mixin({
      computed: {
        /**
         * Map Pinia getters.
         */
        ...mapState(useComponentsStore, {
          getActiveComponents: 'getActive',
          getVisibleComponents: 'getVisible',
        }),
      },

      mounted() {
        const excludedComponents = [
          'async-component-wrapper',
          'base-transition',
        ];

        const handle = convertToHandle(this.$options.name);

        /**
         * Do not push state of global, icon, static, or default Vue components.
         * - Icon components have no name value.
         */
        if (
          !handle ||
          this.cnvsGlobal ||
          this.cnvsStatic ||
          excludedComponents.includes(handle)
        ) {
          return;
        }

        /**
         * If component is specified in the disableComponentByHandle setting
         * then prevent changing to 'active' state and remove element.
         */
        if (cnvs.settings.disableComponentByHandle === handle) {
          this.$el.remove();
          return;
        }

        this.setComponentState({ handle, state: cnvs.states.active });

        /**
         * Mirror state onto the DOM so external tooling (e.g. an e2e suite)
         * can wait on hydration without importing app internals.
         * - Async components have a `[data-component-handle]` wrapper; global
         *   components don't, so fall back to the component's own root
         *   element.
         * - `this.$el` can be a comment placeholder rather than a real
         *   element when the component's template root sits behind a
         *   `v-if` that's still false at mount (e.g. a component gating its
         *   own root on data it hasn't parsed yet). Comment nodes don't
         *   expose `closest()`, so the lookup would otherwise silently
         *   no-op and leave the DOM stuck on whatever state the section had
         *   before Vue mounted. Comment nodes do expose `parentElement`, so
         *   start the search there instead.
         */
        const startElement =
          this.$el?.nodeType === Node.ELEMENT_NODE
            ? this.$el
            : this.$el?.parentElement;

        const target =
          startElement?.closest?.(`[data-component-handle="${handle}"]`) ||
          startElement;

        if (target?.nodeType === Node.ELEMENT_NODE) {
          target.dataset.componentState = cnvs.states.active;
        }
      },

      methods: {
        /**
         * Map Pinia actions.
         * - Some actions are used in canvas-methods.js.
         * - Supposedly unused properties are used in canvas-methods.js.
         */
        ...mapActions(useComponentsStore, {
          // eslint-disable-next-line vue/no-unused-properties
          loadComponent: 'load',
          setComponentState: 'setState',
          // eslint-disable-next-line vue/no-unused-properties
          setComponentStates: 'setStates',
        }),

        /**
         * Get component active state.
         * - Returns true if component has state `active`.
         * - Used to display the Liquid loading state when false.
         * @param {String} handle - Component handle.
         * @returns {Boolean}
         */
        getComponentActive(handle) {
          return this.getActiveComponents.includes(handle);
        },

        /**
         * Get component visible state.
         * - Returns true if component has state and it's not `waiting`.
         * - Used to trigger v-if to prompt Vue to actually load the component.
         * @param {String} handle - Component handle.
         * @returns {Boolean}
         */
        getComponentVisible(handle) {
          return this.getVisibleComponents.includes(handle);
        },
      },
    });
  },
};
